import { useState } from 'react';
import axios from 'axios';
import { TTS_API_URL } from 'routes/api';

const useWordByWordTranscription = () => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState(null);

    const sendTranscriptionData = async (data) => {
        setIsSubmitting(true);
        setError(null);

        try {
            const { consigne, transcriptions, audioFiles, exerciseId, bareme } = data;

            // Préparer les données selon le format demandé par le backend
            const targets = [];

            for (let i = 0; i < consigne.length; i++) {
                const target = {
                    target: consigne[i],
                    audio: audioFiles[i] || null,
                    transcription: transcriptions[i] || "",
                    index: i // Ajouter l'index pour une meilleure traçabilité
                };
                targets.push(target);
            }

            // Créer FormData selon le format demandé
            const formData = new FormData();

            targets.forEach((item, index) => {
                formData.append(`items[${index}][target]`, item.target);
                formData.append(`items[${index}][index]`, item.index.toString());
                if (item.audio) {
                    formData.append(`items[${index}][audio]`, item.audio, `audio_${index}.wav`);
                }
                formData.append(`items[${index}][transcription]`, item.transcription);
            });

            // Ajouter les métadonnées de l'exercice
            formData.append('exerciseId', exerciseId || '');
            formData.append('exerciseType', 'wordByWordTranscription');
            formData.append('bareme', bareme || 0);
            formData.append('totalWords', consigne.length.toString());

            // Object.fromEntries évité : non supporté sur les vieux WebView
            // (Google Go / itel). Reconstruction manuelle équivalente.
            const formDataDebug = {};
            formData.forEach((value, key) => { formDataDebug[key] = value; });
            console.log('Envoi des données au backend:', {
                targets: targets.map(t => ({ target: t.target, index: t.index, hasAudio: !!t.audio })),
                formData: formDataDebug
            });

            // Envoyer au backend
            const response = await axios.post(
                `${TTS_API_URL}/word-by-word-transcription/`,
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                }
            );

            console.log('Réponse du backend:', response.data);

            return {
                success: true,
                data: response.data,
                targets
            };

        } catch (error) {
            console.error('Erreur lors de l\'envoi au backend:', error);
            
            // Vérifier si c'est une erreur d'indisponibilité de l'IA
            if (error?.response?.status === 503 || error?.response?.data?.error === "AI_UNAVAILABLE") {
                const aiError = "Assistant IA momentanément indisponible";
                setError(aiError);
                return {
                    success: false,
                    error: aiError,
                    isAIUnavailable: true,
                    targets: []
                };
            }
            
            const errorMessage = error.message || 'Erreur lors de l\'envoi des données';
            setError(errorMessage);

            return {
                success: false,
                error: errorMessage,
                isAIUnavailable: false,
                targets: []
            };
        } finally {
            setIsSubmitting(false);
        }
    };

    const validateTranscriptionData = (data) => {
        const { consigne, transcriptions } = data;

        if (!consigne || !Array.isArray(consigne) || consigne.length === 0) {
            return { valid: false, error: 'Aucune consigne fournie' };
        }

        if (!transcriptions || !Array.isArray(transcriptions)) {
            return { valid: false, error: 'Aucune transcription fournie' };
        }

        if (transcriptions.length !== consigne.length) {
            return { valid: false, error: 'Le nombre de transcriptions ne correspond pas au nombre de consignes' };
        }

        const emptyTranscriptions = transcriptions.filter(t => !t || t.trim() === '');
        if (emptyTranscriptions.length > 0) {
            return { valid: false, error: 'Toutes les transcriptions doivent être remplies' };
        }

        return { valid: true };
    };

    return {
        sendTranscriptionData,
        validateTranscriptionData,
        isSubmitting,
        error
    };
};

export default useWordByWordTranscription; 